import * as React from 'react';

import { Overlay } from '../overlay/overlay.tsx';

import { Button } from '../button/button.tsx';
import { useDevice, useWindowSize } from '../hooks.tsx';
import { Icon } from '../icon/icon.tsx';
import './regular-modal.css';

interface RegularModalProps extends React.HTMLAttributes<HTMLDivElement> {
  onClose: () => void;
  isOpen: boolean;
  id: string;
  title: string;
  description?: React.ReactNode;
  backCTA?: () => void;
  size?: 'tiny' | 'small' | 'medium' | 'large';
  buttons: {
    variant: 'single' | 'double' | 'stacked' | 'triple' | 'signature';
    list: Array<React.ReactNode>;
  };
}

const RegularModal = React.forwardRef<HTMLDivElement, RegularModalProps>(
  (
    {
      onClose,
      isOpen,
      id,
      title,
      description,
      backCTA,
      size = 'small',
      buttons,
      children,
      ...props
    },
    ref,
  ) => {
    const dimensions = useWindowSize();
    const device = useDevice(dimensions);
    React.useEffect(() => {
      if (typeof window !== 'undefined' && window.document) {
        if (isOpen) {
          document.body.style.overflow = 'hidden';
        } else {
          document.body.style.overflow = 'auto';
        }
      }
    }, [isOpen]);
    return (
      <Overlay onClose={onClose} isOpen={isOpen} id={id}>
        <div
          className={`regular-modal regular-modal-${
            device === 'mobile' ? 'tiny' : size
          }`}
          style={{
            textAlign:
              size === 'large' && description
                ? buttons.variant === 'triple' || buttons.variant === 'stacked'
                  ? 'center'
                  : 'start'
                : 'start',
          }}
          ref={ref}
          {...props}
        >
          <div
            className="regular-modal-actions"
            style={{
              justifyContent: backCTA ? 'space-between' : 'flex-end',
            }}
          >
            {backCTA && (
              <button
                type="button"
                className="regular-modal-actions-back"
                onClick={backCTA}
              >
                <Icon
                  type="arrow-left"
                  size={size === 'tiny' ? 'small' : size}
                  color="var(--color_neutral-darker)"
                />
                Back
              </button>
            )}
            <Button asRemoveIcon="default" onClick={onClose} />
          </div>
          <div className="regular-modal-content">
            <div className="regular-modal-content-title">{title}</div>
            {description && (
              <div className="regular-modal-content-description">
                {description}
              </div>
            )}
            {children && (
              <div className="regular-modal-content-children">{children}</div>
            )}
            {buttons.list.length > 0 && (
              <div
                style={{
                  padding:
                    size === 'medium'
                      ? buttons.variant !== 'double' && device !== 'mobile'
                        ? '0 100px'
                        : '0'
                      : size === 'large'
                      ? description
                        ? buttons.variant === 'single'
                          ? '0 0 0 500px'
                          : buttons.variant === 'double'
                          ? '0 0 0 300px'
                          : buttons.variant === 'stacked' ||
                            buttons.variant === 'triple'
                          ? '0 250px'
                          : '0'
                        : buttons.variant !== 'double' && device !== 'mobile'
                        ? '0 250px'
                        : '0'
                      : '0',
                  flexDirection:
                    ['double', 'signature'].includes(buttons.variant) &&
                    device === 'desktop'
                      ? 'row'
                      : 'column',
                  justifyContent:
                    buttons.variant === 'signature' ? 'center' : 'flex-start',
                }}
                className="regular-modal-content-buttons"
              >
                {buttons.list.map((button, index) => {
                  return (
                    <div
                      key={`button-${
                        // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
                        index
                      }`}
                      className="regular-modal-content-buttons-button"
                    >
                      {button}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </Overlay>
    );
  },
);

export { RegularModal };
